
import React from 'react';
import CalendarEvent from '@/components/CalendarEvent';
import { ProcessedEvent } from '@/utils/calendarEventLayout';
import { CalendarSlot } from '@/utils/calendarSlots';

interface MultiDayEventsLayerProps {
  processedEvents: ProcessedEvent[];
  slots: CalendarSlot[];
  onEventClick: (event: any) => void;
  cellWidth: number;
}

const MultiDayEventsLayer: React.FC<MultiDayEventsLayerProps> = ({
  processedEvents,
  slots,
  onEventClick,
  cellWidth
}) => {
  // Define constants for calculations to avoid magic numbers
  const eventHorizontalPadding = 4; // 2px padding on each side
  const rowHeight = 32; // Height per row
  const verticalOffset = 4; // Small top offset
  const rowGap = 4; // Gap between rows

  // Multi-day events were positioned against the original days[] indices, but
  // now every slot (day or gap) renders at a uniform cellWidth — so translate
  // those original indices into slot indices for pixel-accurate layout.
  const slotIndexForDay = new Map<number, number>();
  slots.forEach((slot, slotIndex) => {
    if (slot.type === 'day') {
      slotIndexForDay.set(slot.originalIndex, slotIndex);
    }
  });

  return (
    <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
      {processedEvents.map((processedEvent, index) => {
        const startSlotIndex = slotIndexForDay.get(processedEvent.startDayIndex);
        const endSlotIndex = slotIndexForDay.get(processedEvent.endDayIndex);

        if (startSlotIndex === undefined || endSlotIndex === undefined) {
          return null;
        }

        const span = endSlotIndex - startSlotIndex + 1;
        const eventWidth = span * cellWidth - (2 * eventHorizontalPadding);
        const leftPosition = startSlotIndex * cellWidth + eventHorizontalPadding;
        const topPosition = (processedEvent.verticalPosition * rowHeight) + verticalOffset;

        return (
          <CalendarEvent
            key={`multi-${processedEvent.event.id}-${index}`}
            event={processedEvent.event}
            onClick={onEventClick}
            cellWidth={cellWidth}
            isMultiDay={true}
            startDayIndex={startSlotIndex}
            endDayIndex={endSlotIndex}
            verticalPosition={processedEvent.verticalPosition}
            style={{
              width: `${eventWidth}px`,
              height: `${rowHeight - rowGap}px`,
              left: `${leftPosition}px`,
              top: `${topPosition}px`,
              position: 'absolute',
              pointerEvents: 'auto',
              zIndex: 10 + processedEvent.verticalPosition
            }}
          />
        );
      })}
    </div>
  );
};

export default MultiDayEventsLayer;
